import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, ChevronRight } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { emprestimosService } from "@/modules/biblioteca/services/emprestimosService";
import { useMeusEmprestimos } from "@/modules/biblioteca/hooks/useEmprestimos";

/** true se a data (YYYY-MM-DD ou ISO) cai entre hoje e daqui a `dias` dias. */
function dentroDosProximosDias(data: string, dias: number): boolean {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const limite = new Date(hoje);
  limite.setDate(limite.getDate() + dias);
  limite.setHours(23, 59, 59, 999);
  const d = new Date(data);
  return d >= hoje && d <= limite;
}

interface Tile {
  valor: number;
  rotulo: string;
  cor: string;
}

/**
 * Preview da Biblioteca na Home. É "papel-aware":
 * - Gestor/Admin: Visão Geral do módulo (empréstimos ativos, devoluções da
 *   semana, atrasados).
 * - Membro comum: os seus próprios números (livros lidos, empréstimos ativos)
 *   — os mesmos do seu perfil.
 * Só aparece se a pessoa enxerga o módulo Biblioteca (regra de Visibilidade)
 * e se há algo a mostrar: enquanto carrega, ou quando todos os números são
 * zero, a seção fica oculta (mesmo comportamento do preview de Comunicados).
 */
export function PreviewBibliotecaHome() {
  const navigate = useNavigate();
  const { pessoa, ehAdminPlataforma, podeGerenciarBiblioteca, temAcessoAdministrativo, modulosVisiveis } =
    useAuth();

  const gerente = ehAdminPlataforma || podeGerenciarBiblioteca;
  const veBiblioteca = modulosVisiveis.includes("biblioteca");

  // Gestor: todos os empréstimos (RLS libera). Membro: só os próprios.
  const overview = useQuery({
    queryKey: ["emprestimos"],
    queryFn: emprestimosService.listarTodos,
    enabled: veBiblioteca && gerente,
  });
  const meus = useMeusEmprestimos(veBiblioteca && !gerente ? pessoa?.id : undefined);

  const tiles = useMemo<Tile[]>(() => {
    if (gerente) {
      const todos = overview.data ?? [];
      const ativos = todos.filter((e) => e.status === "EM_ABERTO" || e.status === "ATRASADO");
      const atrasados = todos.filter((e) => e.status === "ATRASADO").length;
      const devolucoes = ativos.filter(
        (e) => e.data_devolucao_prevista && dentroDosProximosDias(e.data_devolucao_prevista, 7),
      ).length;
      return [
        { valor: ativos.length, rotulo: "Em andamento", cor: "text-text" },
        { valor: devolucoes, rotulo: "Devoluções (7 dias)", cor: "text-warning" },
        { valor: atrasados, rotulo: "Atrasados", cor: "text-danger" },
      ];
    }
    const lista = meus.data ?? [];
    const lidos = lista.filter((e) => e.status === "DEVOLVIDO").length;
    const ativos = lista.filter((e) => e.status === "EM_ABERTO" || e.status === "ATRASADO").length;
    return [
      { valor: lidos, rotulo: "Livros devolvidos", cor: "text-text" },
      { valor: ativos, rotulo: "Em andamento", cor: "text-primary" },
    ];
  }, [gerente, overview.data, meus.data]);

  const carregando = gerente ? overview.isLoading : meus.isLoading;
  const semDados = tiles.every((t) => t.valor === 0);
  if (!veBiblioteca || carregando || semDados) return null;

  const destino = temAcessoAdministrativo ? "/admin" : "/usuario";

  return (
    <section className="mb-4 rounded-2xl border border-border bg-surface p-4 sm:p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-bold text-text">
          <BookOpen size={18} className="text-primary" /> Biblioteca
        </h2>
        <button
          onClick={() => navigate(destino)}
          className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline"
        >
          Abrir <ChevronRight size={16} />
        </button>
      </div>
      <div className={`grid gap-3 ${gerente ? "grid-cols-3" : "grid-cols-2"}`}>
        {tiles.map((t) => (
          <div key={t.rotulo} className="rounded-xl bg-surface-2 p-3">
            <p className={`text-2xl font-black ${t.cor}`}>{t.valor}</p>
            <p className="mt-0.5 text-xs text-muted">{t.rotulo}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
